import React, { useState } from 'react';
import { RestHelper } from '../../helper/restHelper';

const AdminInvoiceSearch = ({ firms = [] }) => {
  const [searchMode, setSearchMode] = useState('firm');

  // Firm search state
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Invoice number search state
  const [invoiceNumber, setInvoiceNumber] = useState('');

  // Results state
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const api = RestHelper();

  const toEpoch = (dateStr, endOfDay = false) => {
    const d = new Date(dateStr);
    if (endOfDay) d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
  };

  const formatDate = (epoch) => {
    if (!epoch) return '—';
    return new Date(epoch * 1000).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatDateTime = (epoch) => {
    if (!epoch) return '—';
    return new Date(epoch * 1000).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const clearState = () => {
    setSelectedFirmId('');
    setStartDate('');
    setEndDate('');
    setInvoiceNumber('');
    setSearchResults([]);
    setError('');
    setHasSearched(false);
  };

  const handleSearch = () => {
    setError('');
    setSearchResults([]);
    setHasSearched(false);

    let reqData;

    if (searchMode === 'firm') {
      if (!selectedFirmId) { setError('Please select a firm'); return; }
      if (!startDate) { setError('Please select a start date'); return; }
      if (!endDate) { setError('Please select an end date'); return; }
      if (new Date(endDate) < new Date(startDate)) {
        setError('End date must be on or after start date'); return;
      }
      reqData = {
        action: 'search',
        firm_id: selectedFirmId,
        start_date: toEpoch(startDate),
        end_date: toEpoch(endDate, true)
      };
    } else {
      const num = parseInt(invoiceNumber, 10);
      if (isNaN(num) || num < 1000 || num > 10000) {
        setError('Please enter a valid invoice number (1000–10000)'); return;
      }
      reqData = { action: 'lookup', invoice_number: num };
    }

    setIsLoading(true);
    api
      .postCreate('/get-invoice-file', {}, reqData)
      .then(res => {
        // Normalize: search returns { invoices: [] }, lookup returns same shape or single object
        const invoices = res.invoices
          ? res.invoices
          : res.invoice ? [res.invoice] : [];
        setSearchResults(invoices);
        setHasSearched(true);
        setIsLoading(false);
      })
      .catch(err => {
        console.log(err);
        setError('Error searching for invoices');
        setIsLoading(false);
      });
  };

  const handleDownload = (invoiceNum) => {
    api
      .postCreate('/get-invoice-file', {}, { action: 'download', invoice_number: invoiceNum })
      .then(res => window.open(res.get_url, '_blank'))
      .catch(err => console.log(err));
  };

  const statusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      void: 'bg-red-100 text-red-700',
    };
    const key = (status || '').toLowerCase();
    const cls = styles[key] || 'bg-neutral-100 text-neutral-600';
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
        {status || '—'}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-neutral-200 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-neutral-200">
        <h2 className="text-lg font-semibold text-neutral-800">Invoice Search</h2>
        <div className="w-12 h-1 bg-blue-500 rounded mt-2"></div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Mode Toggle */}
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => { setSearchMode('firm'); clearState(); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              searchMode === 'firm'
                ? 'bg-blue-600 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            By Firm &amp; Date
          </button>
          <button
            onClick={() => { setSearchMode('invoice'); clearState(); }}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              searchMode === 'invoice'
                ? 'bg-blue-600 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            By Invoice #
          </button>
        </div>

        {/* Search Inputs */}
        {searchMode === 'firm' ? (
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Firm</label>
              <select
                value={selectedFirmId}
                onChange={(e) => setSelectedFirmId(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
              >
                <option value="">Select a firm...</option>
                {firms.map(f => (
                  <option key={f.org_id} value={f.org_id}>{f.org_name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 text-white font-medium rounded-lg transition-colors"
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
              <button
                onClick={clearState}
                className="px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-medium rounded-lg border border-neutral-300 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[150px] max-w-[250px]">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Invoice Number</label>
              <input
                type="number"
                min="1000"
                max="10000"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="1000–10000"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSearch}
                disabled={isLoading || !invoiceNumber}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-neutral-300 text-white font-medium rounded-lg transition-colors"
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
              <button
                onClick={clearState}
                className="px-4 py-2 bg-neutral-100 hover:bg-neutral-200 text-neutral-700 font-medium rounded-lg border border-neutral-300 transition-colors"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Results */}
        {!isLoading && searchResults.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-neutral-600">
              Found {searchResults.length} invoice{searchResults.length !== 1 ? 's' : ''}
            </h3>
            {searchResults.map((invoice) => (
              <div key={invoice.invoice_number} className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div className="space-y-1">
                    <div className="font-semibold text-neutral-800">
                      Invoice #{invoice.invoice_number}
                    </div>
                    <div className="text-sm text-neutral-600">
                      <span className="font-medium">Title Company:</span> {invoice.title_company || '—'}
                    </div>
                    <div className="text-sm text-neutral-600">
                      <span className="font-medium">File Number:</span> {invoice.file_number || '—'}
                    </div>
                    <div className="text-sm text-neutral-600">
                      <span className="font-medium">Appointment:</span> {formatDateTime(invoice.appt_numerical_time)}
                    </div>
                    <div className="text-sm text-neutral-600">
                      <span className="font-medium">Created:</span> {formatDate(invoice.created_at)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {statusBadge(invoice.status)}
                    <button
                      onClick={() => handleDownload(invoice.invoice_number)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                    >
                      View Invoice
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results */}
        {!isLoading && hasSearched && searchResults.length === 0 && !error && (
          <div className="text-center py-8 text-neutral-500">
            No invoices found for the selected criteria.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoiceSearch;
